import { useStore } from '../lib/store.ts'
import { Bar, Tile } from '../components/ui.tsx'
import { dayKey, humanDays } from '../lib/time.ts'
import { summarize } from '../lib/srs.ts'
import { navigate } from '../lib/router.ts'
import { plural } from '../lib/text.ts'

export function Home() {
  const { idx, cards, session, daily, streak, settings, now } = useStore()
  const today = daily[dayKey(now)] ?? { introduced: 0, reviewed: 0, seconds: 0 }
  const doneToday = today.introduced + today.reviewed
  const planned = session.due.length + session.fresh.length
  const total = idx.order.length
  const retention = summarize(
    idx.order.map((id) => cards.get(id) ?? { id, state: 'new' as const, step: 0, interval: 0, ease: 2.5, due: 0, reps: 0, lapses: 0, introduced: null, lastReview: null }),
    now,
  )
  const introduced = total - session.newRemaining
  const nothingLeft = planned === 0

  return (
    <div className="screen stack">
      <header className="stack--tight stack" style={{ paddingTop: 8 }}>
        <div className="badge-day">
          <span>{greeting(now)}</span>
          {streak > 0 && <span>· 🔥 {streak} {plural(streak, 'день', 'дні', 'днів')} поспіль</span>}
        </div>
        <h1>{nothingLeft ? 'На сьогодні все' : 'Заняття на сьогодні'}</h1>
      </header>

      <div className="card stack">
        {nothingLeft ? (
          <>
            <p className="muted">
              Сьогоднішня норма закрита: {today.introduced} нових, {today.reviewed} повторень.
              Наступні картки зʼявляться завтра.
            </p>
            <button className="btn btn--wide" onClick={() => navigate('/modules')}>
              Повторити якийсь модуль
            </button>
          </>
        ) : (
          <>
            <div className="row" style={{ gap: 20 }}>
              <div>
                <div className="tile__value">{session.fresh.length}</div>
                <div className="tile__label">нових фраз</div>
              </div>
              <div>
                <div className="tile__value">{session.due.length}</div>
                <div className="tile__label">на повторення</div>
              </div>
              <div className="spacer" />
              <div className="center">
                <div className="tile__value">~{Math.max(1, session.estimateMinutes)}</div>
                <div className="tile__label">хвилин</div>
              </div>
            </div>

            {doneToday > 0 && (
              <>
                <Bar value={doneToday} max={doneToday + planned} good />
                <div className="tiny faint">Сьогодні вже пройдено: {doneToday}</div>
              </>
            )}

            <button className="btn btn--primary btn--wide" onClick={() => navigate('/session')}>
              {doneToday > 0 ? 'Продовжити заняття' : 'Почати заняття'}
            </button>
          </>
        )}
      </div>

      {session.backlog > 0 && (
        <div className="card">
          <div className="row" style={{ alignItems: 'flex-start' }}>
            <span style={{ fontSize: 20 }}>⏳</span>
            <div>
              <div style={{ fontWeight: 570 }}>Накопичився хвіст</div>
              <p className="small muted" style={{ marginTop: 4 }}>
                Ще {session.backlog} карток чекають понад сьогоднішню норму. Планувальник
                тимчасово зменшить порцію нового, щоб ти наздогнав.
              </p>
            </div>
          </div>
        </div>
      )}

      {!session.onTrack && (
        <div className="card">
          <div className="row" style={{ alignItems: 'flex-start' }}>
            <span style={{ fontSize: 20 }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 570 }}>Темп не встигає за дедлайном</div>
              <p className="small muted" style={{ marginTop: 4 }}>
                Щоб пройти курс до {settings.deadline}, потрібно більше нових фраз на день,
                ніж вміщується у заняття. Подивись план.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="tiles">
        <Tile value={retention.known} label="знаю впевнено" />
        <Tile value={retention.learning} label="в роботі" />
        <Tile value={session.newRemaining} label="попереду" />
      </div>

      <div className="card stack--tight stack">
        <div className="row row--between">
          <strong>До поїздки</strong>
          <span className="muted small">
            {Number.isFinite(session.daysLeft) ? humanDays(session.daysLeft) : '—'}
          </span>
        </div>
        <Bar value={introduced} max={total} />
        <div className="tiny faint">
          Пройдено {introduced} з {total} фраз курсу · норма {session.newPerDay} нових на день
        </div>
      </div>

      <PracticeOfTheDay />

      <div className="list">
        <a className="item" href="#/modules">
          <span className="item__emoji" aria-hidden>📚</span>
          <span className="item__body">
            <span className="item__title">Модулі курсу</span>
            <span className="item__sub">Діалоги, рольові сцени й пояснення</span>
          </span>
          <span className="item__after">→</span>
        </a>
        <a className="item" href="#/exam">
          <span className="item__emoji" aria-hidden>🎓</span>
          <span className="item__body">
            <span className="item__title">Екзамен-симуляція</span>
            <span className="item__sub">День туриста в Польщі, всі ситуації підряд</span>
          </span>
          <span className="item__after">→</span>
        </a>
      </div>
    </div>
  )
}

/**
 * Практика дня: картки закривають лексику, але говоріння з них не виростає.
 * Тому поруч завжди лежить діалог і рольова сцена того модуля,
 * який учень проходить саме зараз.
 */
function PracticeOfTheDay() {
  const { idx, cards, session } = useStore()

  // Поточний модуль — той, звідки йдуть сьогоднішні нові фрази,
  // а якщо нового немає, то останній, який уже почали.
  const currentId =
    (session.fresh[0] && idx.moduleOfPhrase.get(session.fresh[0])) ??
    [...idx.modules].reverse().find((m) => m.phrases.some((p) => cards.has(p.id)))?.id
  const m = currentId ? idx.moduleById.get(currentId) : idx.modules[0]
  if (!m) return null

  const dialogue = m.dialogues[0]
  const roleplay = m.roleplays[0]
  const insight = m.insights[0]

  return (
    <section className="stack--tight stack">
      <div className="row row--between">
        <h3>Практика дня</h3>
        <a className="tiny" style={{ color: 'var(--accent)', textDecoration: 'none' }} href={`#/module/${m.id}`}>
          {m.emoji} {m.title}
        </a>
      </div>
      <p className="tiny faint">
        Картки дають слова, а говоріння виростає ось із цього. Пройди після заняття —
        це ще 10–15 хвилин.
      </p>
      <div className="list">
        {insight && (
          <a className="item" href={`#/insight/${m.id}/${insight.id}`}>
            <span className="item__emoji" aria-hidden>💡</span>
            <span className="item__body">
              <span className="item__title">{insight.title}</span>
              <span className="item__sub">Як це влаштовано</span>
            </span>
            <span className="item__after">→</span>
          </a>
        )}
        {dialogue && (
          <a className="item" href={`#/dialogue/${m.id}/${dialogue.id}`}>
            <span className="item__emoji" aria-hidden>💬</span>
            <span className="item__body">
              <span className="item__title">{dialogue.title}</span>
              <span className="item__sub">Діалог: читання, аудіювання, практика</span>
            </span>
            <span className="item__after">→</span>
          </a>
        )}
        {roleplay && (
          <a className="item" href={`#/roleplay/${m.id}/${roleplay.id}`}>
            <span className="item__emoji" aria-hidden>🎭</span>
            <span className="item__body">
              <span className="item__title">{roleplay.title}</span>
              <span className="item__sub">Рольова сцена: {roleplay.role}</span>
            </span>
            <span className="item__after">→</span>
          </a>
        )}
        <a className="item" href="#/pairs">
          <span className="item__emoji" aria-hidden>👂</span>
          <span className="item__body">
            <span className="item__title">Розрізнення звуків</span>
            <span className="item__sub">Мінімальні пари: ł/l, sz/ś, носові</span>
          </span>
          <span className="item__after">→</span>
        </a>
        <a className="item" href={`#/chat/${m.id}`}>
          <span className="item__emoji" aria-hidden>🗣️</span>
          <span className="item__body">
            <span className="item__title">Вільна розмова</span>
            <span className="item__sub">Без сценарію, з мʼякими виправленнями</span>
          </span>
          <span className="item__after">→</span>
        </a>
      </div>
    </section>
  )
}

function greeting(now: number): string {
  const h = new Date(now).getHours()
  if (h < 5) return 'Доброї ночі'
  if (h < 12) return 'Доброго ранку'
  if (h < 18) return 'Добрий день'
  return 'Добрий вечір'
}

